/**
 * Vertical Template Selection Step (Step 2)
 *
 * Presents template options for vertical configuration:
 * - Health Insurance
 * - Telco Mobile
 * - Energy Electricity
 * - Blank (start from scratch)
 *
 * Multiple verticals can be selected.
 */

import { Heart, Smartphone, Zap, FileText, Check } from "lucide-react";
import type { ReactElement } from "react";
import { useCallback } from "react";

export interface VerticalTemplate {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly icon: "health" | "telco" | "energy" | "blank";
  readonly configs: readonly string[];
}

const ICON_MAP = {
  health: Heart,
  telco: Smartphone,
  energy: Zap,
  blank: FileText,
} as const;

export const VERTICAL_TEMPLATES: readonly VerticalTemplate[] = [
  {
    id: "health-insurance",
    name: "Health Insurance",
    description: "Compare hospital, extras, and combined health insurance policies.",
    icon: "health",
    configs: ["Schema", "Eligibility", "Ranking", "Journey", "Branding"],
  },
  {
    id: "telco-mobile",
    name: "Telco Mobile",
    description: "Compare mobile phone plans across carriers and plan types.",
    icon: "telco",
    configs: ["Schema", "Eligibility", "Ranking", "Journey", "Branding"],
  },
  {
    id: "energy-electricity",
    name: "Energy Electricity",
    description: "Compare electricity providers and usage-based pricing.",
    icon: "energy",
    configs: ["Schema", "Eligibility", "Ranking", "Journey", "Branding"],
  },
  {
    id: "blank",
    name: "Blank",
    description: "Start from scratch with an empty configuration.",
    icon: "blank",
    configs: [],
  },
] as const;

interface VerticalTemplateStepProps {
  readonly selectedTemplates: readonly string[];
  readonly onChange: (templates: readonly string[]) => void;
}

export function VerticalTemplateStep({
  selectedTemplates,
  onChange,
}: VerticalTemplateStepProps): ReactElement {
  const handleToggle = useCallback(
    (templateId: string): void => {
      if (selectedTemplates.includes(templateId)) {
        onChange(selectedTemplates.filter((id) => id !== templateId));
      } else {
        onChange([...selectedTemplates, templateId]);
      }
    },
    [selectedTemplates, onChange]
  );

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Vertical Templates</h2>
        <p className="mt-1 text-sm text-foreground-muted">
          Select one or more vertical templates to pre-configure. You can add more later.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        {VERTICAL_TEMPLATES.map((template) => {
          const isSelected = selectedTemplates.includes(template.id);
          const IconComponent = ICON_MAP[template.icon];

          return (
            <button
              key={template.id}
              type="button"
              onClick={() => handleToggle(template.id)}
              data-testid={`template-card-${template.id}`}
              className={`relative flex flex-col items-start rounded-lg border p-4 text-left transition-colors ${
                isSelected
                  ? "border-primary bg-primary/5 ring-1 ring-primary"
                  : "border-border bg-background hover:border-primary/50 hover:bg-background-secondary"
              }`}
            >
              {/* Selected check */}
              {isSelected && (
                <div className="absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-white">
                  <Check className="h-3 w-3" />
                </div>
              )}

              {/* Icon */}
              <div
                className={`flex h-10 w-10 items-center justify-center rounded-lg ${
                  isSelected
                    ? "bg-primary/10 text-primary"
                    : "bg-background-secondary text-foreground-muted"
                }`}
              >
                <IconComponent className="h-5 w-5" />
              </div>

              {/* Name */}
              <h3 className="mt-3 font-medium">{template.name}</h3>

              {/* Description */}
              <p className="mt-1 text-sm text-foreground-muted">{template.description}</p>

              {/* Configs that will be seeded */}
              {template.configs.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1">
                  {template.configs.map((config) => (
                    <span
                      key={config}
                      className="rounded bg-background-secondary px-1.5 py-0.5 text-xs text-foreground-muted"
                    >
                      {config}
                    </span>
                  ))}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
